import { useRef, useEffect, useState, type FC, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

interface ShadowContainerProps {
  children: ReactNode;
  /** CSS string to inject into the shadow root */
  styles?: string;
  /** Optional class name for the shadow host element */
  className?: string;
  /** Optional inline styles for the shadow host element */
  style?: CSSProperties;
  /** Tag name for the shadow host element */
  as?: 'div' | 'span' | 'section' | 'article' | 'aside' | 'main' | 'nav' | 'header' | 'footer';
}

/**
 * ShadowContainer wraps children in a Shadow DOM boundary for complete CSS isolation.
 * 
 * This component:
 * - Creates a shadow root on mount
 * - Injects provided CSS styles into the shadow root
 * - Renders children inside the shadow DOM via portal
 * - Prevents host app styles from affecting SDK components
 * - Prevents SDK styles from leaking to host app
 */
export const ShadowContainer: FC<ShadowContainerProps> = ({
  children,
  styles = '',
  className,
  style,
  as: Tag = 'div',
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Check if shadow root already exists (React strict mode double-mount)
    let shadow = host.shadowRoot;
    if (!shadow) {
      shadow = host.attachShadow({ mode: 'open' });
    }

    setShadowRoot(shadow);
  }, []);

  // Inject styles into shadow root
  useEffect(() => {
    if (!shadowRoot || !styles) return;

    // Check if style element already exists
    const existingStyle = shadowRoot.querySelector('style[data-aria-styles]');
    if (existingStyle) {
      existingStyle.textContent = styles;
      return;
    }

    // Create and inject style element
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-aria-styles', 'true');
    styleElement.textContent = styles;
    shadowRoot.prepend(styleElement);

    return () => {
      styleElement.remove();
    };
  }, [shadowRoot, styles]);

  // Create a container div inside shadow root for React portal
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!shadowRoot) return;

    // Check if container already exists
    let container = shadowRoot.querySelector('.aria-shadow-content') as HTMLDivElement;
    if (!container) {
      container = document.createElement('div');
      container.className = 'aria-shadow-content';
      shadowRoot.appendChild(container);
    }

    setPortalContainer(container);
  }, [shadowRoot]);

  return (
    <Tag
      ref={hostRef as React.RefObject<HTMLDivElement>}
      className={className}
      style={style}
    >
      {portalContainer && createPortal(children, portalContainer)}
    </Tag>
  );
};

export default ShadowContainer;
